import React, { useMemo, useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Row, Input, Button, Dropdown, DatePicker, Table } from "antd";
import type { TableColumnsType } from "antd";
import { useDebounce } from "@react-hook/debounce";
import { ApolloError } from "@apollo/client";
import dayjs from "dayjs";
import { useShallow } from "zustand/react/shallow";
import { HiSearch } from "react-icons/hi";

import InstalledProductCard from "components/installed-product-card/installed-product-card";
import ViewFilter from "components/view-filter/view-filter";
import ScrollToTopButton from "components/scroll-to-top-button/scroll-to-top-button";
import StructuredLocation from "components/structured-location/structured-location";

import { useGetFilteredInstalledProductsQuery } from "graphql/queries/get-filtered-installed-products.generated";
import {
	type GetDistinctFilteredInstalledProductsQuery as iDeviceQueryType,
	useGetDistinctFilteredInstalledProductsQuery
} from "graphql/queries/get-distinct-filtered-installed-products.generated";
import { useExternal_DevicesMutation } from "graphql/mutations/sync-external-devices.generated";
import { useLoggedUser } from "state/reactive-vars/logged-user-var";
import { useInstalledProductsFiltersStore } from "state/zustand-stores/installed-products-filters-store";

import routeNames from "constants/route-names";
import { DEVICE_STATUS_FILTER_OPTIONS } from "constants/constants";
import { SEARCH_DEBOUNCE_DELAY_MS } from "constants/constants";
import { createLabelForFilters } from "utils/helpers";
import { buildCheckboxMenuItems } from "utils/filter-helpers";
import { labelsFromRelationships } from "utils/location-hierarchy";
import { getDeviceStatusLabel } from "utils/device-status";

type iDeviceQueryTypeWithPropertyCount = (iDeviceQueryType["installed_products"][number] & {
	property_count: number;
})[];
const { RangePicker } = DatePicker;
const columns = [
	{
		title: "PROPERTY",
		dataIndex: "property",
		render: item => {
			return item?.name || "";
		},
		key: `PROPERTY`
	},
	{
		title: "DEVICE",
		dataIndex: "product",
		render: item => {
			return item?.name || "";
		},
		key: `PRODUCT`
	},
	{
		title: "LOCATION",
		dataIndex: "location",
		render: (item, record: any) => {
			const structuredLocationLabel = labelsFromRelationships(record);
			const locationLabel = item ? item : structuredLocationLabel ? null : "N/A";

			return (
				<div>
					<StructuredLocation labels={structuredLocationLabel} />
					{locationLabel}
				</div>
			);
		},
		key: `LOCATION`
	},
	{
		title: "INSTALLED",
		dataIndex: "installation_date",
		render: item => {
			const formattedDate = dayjs(item).format("MM/DD/YYYY");
			return formattedDate;
		},
		key: `INSTALLED`
	},
	{
		title: "SERIAL",
		dataIndex: "product_serial",
		key: `SERIAL`
	},
	{
		title: "TYPE",
		dataIndex: "product",
		render: item => {
			return item?.product_category?.label || "";
		},
		key: `TYPE`
	},
	{
		title: "STATUS",
		dataIndex: "device_status",
		key: `STATUS`,
		render: (status, device) => {
			let color = "#A0A0A0"; // default gray for unknown
			if (status === "online") color = "#26D029"; // green
			if (status === "offline") color = "#F5222D"; // red
			if (status === "firmware updated") color = "#0047FF"; // blue

			return (
				<span style={{ color, fontWeight: 500, textTransform: "uppercase" }}>
					{getDeviceStatusLabel(device) || "NOT AVAILABLE"}
				</span>
			);
		}
	}
	// {
	//   title: "WARRANTY",
	//   dataIndex: "warranty_status",
	//   key: `WARRANTY`,
	// },
];

const InstalledProducts: React.FC = () => {
	const navigate = useNavigate();
	const { loggedUserValue } = useLoggedUser();

	const [propertiesDropdownOpen, setPropertiesDropdownOpen] = useState<boolean>(false);
	const [categoriesDropdownOpen, setCategoriesDropdownOpen] = useState<boolean>(false);
	const [deviceStatusDropdownOpen, setDeviceStatusDropdownOpen] = useState<boolean>(false);
	const [productCategories, setProductCategories] = useState<Map<string, string>>();
	const [syncing, setSyncing] = useState(false);

	const [dataSource, setDataSource] = useState<iDeviceQueryTypeWithPropertyCount>([]);
	const [dataSourceList, setDataSourceList] = useState<iDeviceQueryTypeWithPropertyCount>([]);
	const [tableColumns, setTableColumns] = useState<TableColumnsType>(columns);

	const scrollContainerRef = useRef<HTMLDivElement>(null);
	const lastSyncedPropertiesRef = useRef<string[]>([]);
	// Guard to initialize property filter only once on mount
	const isPropertiesInitialized = useRef(false);

	// Efficiently select only the necessary parts of the state
	const gridView = useInstalledProductsFiltersStore(state => state.filters.gridView);
	const selectedPropertiesFilter = useInstalledProductsFiltersStore(state => state.filters.selectedPropertiesFilter);

	const selectedCategoriesFilter = useInstalledProductsFiltersStore(state => state.filters.selectedCategoriesFilter);
	const selectedProductsFilter = useInstalledProductsFiltersStore(state => state.filters.selectedProductsFilter);
	const selectedWarrantyStatusFilter = useInstalledProductsFiltersStore(
		state => state.filters.selectedWarrantyStatusFilter
	);
	const selectedDeviceStatusFilter = useInstalledProductsFiltersStore(
		state => state.filters.selectedDeviceStatusFilter
	);
	const installationDateRange = useInstalledProductsFiltersStore(
		useShallow(state => ({
			start: state.filters.installationDateStart,
			end: state.filters.installationDateEnd
		}))
	);

	// Get actions from the store
	const setGridView = useInstalledProductsFiltersStore(state => state.setGridView);
	const setPropertyFilters = useInstalledProductsFiltersStore(state => state.setPropertyFilters);
	const setCategoryFilters = useInstalledProductsFiltersStore(state => state.setCategoryFilters);
	// const setWarrantyStatusFilters = useInstalledProductsFiltersStore(
	//   (state) => state.setWarrantyStatusFilters
	// );
	const setDeviceStatusFilters = useInstalledProductsFiltersStore(state => state.setDeviceStatusFilters);
	const setDateRange = useInstalledProductsFiltersStore(state => state.setDateRange);

	const searchTerm = useInstalledProductsFiltersStore(state => state.filters.searchInstalledProductsTerm);

	const setSearchTerm = useInstalledProductsFiltersStore(state => state.setSearchTerm);

	const [debouncedSearchTerm, setDebouncedSearchTerm] = useDebounce(searchTerm, SEARCH_DEBOUNCE_DELAY_MS);

	useEffect(() => {
		setDebouncedSearchTerm(searchTerm);
	}, [searchTerm]);

	const productsFilter = useMemo(() => {
		const whereClause: any = {
			deleted_at: { _is_null: true },
			_or: [{ handover: { status: { _eq: "PUBLISHED" } } }, { handover_id: { _is_null: true } }]
		};

		// only add db filter if the user selected SOME properties
		if (
			selectedPropertiesFilter.length !== 0 &&
			selectedPropertiesFilter.length !== loggedUserValue.user_properties.length
		) {
			whereClause.property_id = { _in: selectedPropertiesFilter };
		} else {
			const userPropertiesIds = loggedUserValue?.user_properties?.map(up => up.property_id) || [];
			whereClause.property_id = { _in: userPropertiesIds };
		}

		if (selectedCategoriesFilter.length) {
			whereClause.product = {
				_and: [{ type: { _in: selectedCategoriesFilter } }]
			};
		} else {
			whereClause.product = {};
		}

		if (selectedProductsFilter.length) {
			whereClause.product_id = { _in: selectedProductsFilter };
		}

		if (selectedWarrantyStatusFilter.length) {
			whereClause.warranty_status = {
				_in: selectedWarrantyStatusFilter
			};
		}

		if (selectedDeviceStatusFilter.length) {
			const nonNullStatuses = selectedDeviceStatusFilter.filter(s => s !== "null");
			const includesNull = selectedDeviceStatusFilter.includes("null");

			if (nonNullStatuses.length && includesNull) {
				if (!whereClause._and) whereClause._and = [];
				whereClause._and.push({
					_or: [{ device_status: { _in: nonNullStatuses } }, { device_status: { _is_null: true } }]
				});
			} else if (nonNullStatuses.length) {
				whereClause.device_status = { _in: nonNullStatuses };
			} else {
				whereClause.device_status = { _is_null: true };
			}
		}

		if (installationDateRange.start || installationDateRange.end) {
			whereClause.installation_date = {};
			if (installationDateRange.start) {
				whereClause.installation_date._gte = installationDateRange.start.toISOString();
			}
			if (installationDateRange.end) {
				whereClause.installation_date._lte = installationDateRange.end.toISOString();
			}
		}

		if (debouncedSearchTerm) {
			const searchOr = [
				{ product_serial: { _ilike: `%${debouncedSearchTerm}%` } },
				{ location: { _ilike: `%${debouncedSearchTerm}%` } },
				{ product_notes: { _ilike: `%${debouncedSearchTerm}%` } },
				{ product: { name: { _ilike: `%${debouncedSearchTerm}%` } } },
				{ product: { description: { _ilike: `%${debouncedSearchTerm}%` } } },
				{ product: { make: { _ilike: `%${debouncedSearchTerm}%` } } },
				{ product: { model: { _ilike: `%${debouncedSearchTerm}%` } } },
				{ product: { sku: { _ilike: `%${debouncedSearchTerm}%` } } },
				{ product: { product_notes: { _ilike: `%${debouncedSearchTerm}%` } } },
				{
					product: {
						product_attachments: {
							transcription: { _ilike: `%${debouncedSearchTerm}%` }
						}
					}
				}
			];

			if (!whereClause._and) whereClause._and = [];
			whereClause._and.push({ _or: searchOr });
		}

		return whereClause;
	}, [
		loggedUserValue,
		selectedPropertiesFilter,
		selectedCategoriesFilter,
		selectedProductsFilter,
		selectedWarrantyStatusFilter,
		selectedDeviceStatusFilter,
		installationDateRange.start,
		installationDateRange.end,
		debouncedSearchTerm
	]);

	const handleOnClickDevice = useCallback(
		recordId => {
			const path = `${routeNames.INSTALLED_DEVICES_DETAILS.replace(":installedDevId", recordId)}`;

			navigate(path);
		},
		[navigate]
	);

	const handleSearchTermChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			// Directly update the search term in Zustand
			const value = event.target.value;
			setSearchTerm(value);
		},
		[setSearchTerm]
	);

	const handleOnSelectPropertiesFilter = useCallback(
		(selectedKeys: string[]) => {
			if (selectedKeys.includes("property-dropdown-option-all")) {
				setPropertyFilters([]);
				return;
			}

			setPropertyFilters(selectedKeys);
		},
		[setPropertyFilters]
	);

	const handleOnSelectCategoriesFilter = useCallback(
		(selectedKeys: string[]) => {
			if (selectedKeys.includes("category-dropdown-option-all")) {
				setCategoryFilters([]);
				return;
			}

			setCategoryFilters(selectedKeys);
		},
		[setCategoryFilters]
	);

	const handleOnSelectDeviceStatusFilter = useCallback(
		(selectedKeys: string[]) => {
			if (selectedKeys.includes("device-status-dropdown-option-all")) {
				setDeviceStatusFilters([]);
				return;
			}

			setDeviceStatusFilters(selectedKeys);
		},
		[setDeviceStatusFilters]
	);

	// const handleOnSelectStatusFilter = useCallback(
	//   (args) => {
	//     const { selectedKeys } = args;

	//     if (selectedKeys.includes("status-dropdown-option-all")) {
	//       setWarrantyStatusFilters([]);
	//       return;
	//     }

	//     setWarrantyStatusFilters(selectedKeys);
	//   },
	//   [setWarrantyStatusFilters]
	// );

	const [externalDevicesMutation, { loading: loadingExternalDevices }] = useExternal_DevicesMutation({
		errorPolicy: "none",
		notifyOnNetworkStatusChange: true
	});

	const handleOnErrorGetFilteredInstalledProducts = useCallback((error: ApolloError) => {
		console.log("useGetFilteredInstalledProductsQuery Error: ", error);
	}, []);

	const handleOnCompleteGetDistinctFilteredInstalledProducts = useCallback(
		(data: iDeviceQueryType) => {
			const { installed_products, product_categories, Distinct_Properties_By_Product_By_User_Id } = data || {};

			// console.log("Complete DISTINCT Filtered Installed Products data: ", data);

			// this matches installed products with their property count
			const productDetailsMap = new Map(
				(Distinct_Properties_By_Product_By_User_Id || []).map(item => [item.product_id, item])
			);

			const mergedProducts = installed_products.map(installedProduct => {
				const productDetails = productDetailsMap.get(installedProduct.product_id);

				if (!productDetails) {
					console.warn(`No device details found for product_id: ${installedProduct.product_id}`);
					return installedProduct;
				}

				return {
					...installedProduct,
					property_count: (productDetails as any).property_count
				};
			});

			mergedProducts.sort((a, b) => {
				const nameA = a.product.name.toLowerCase();
				const nameB = b.product.name.toLowerCase();
				if (nameA < nameB) return -1;
				if (nameA > nameB) return 1;
				return 0;
			});

			setDataSource(mergedProducts as iDeviceQueryTypeWithPropertyCount);
			const productCategoryMap = new Map();
			product_categories.forEach(v => {
				productCategoryMap.set(v.name, v.label);
			});

			setProductCategories(productCategoryMap);
		},
		[productsFilter]
	);

	const handleOnCompleteGetFilteredInstalledProducts = useCallback(data => {
		const { installed_products, product_categories } = data || {};

		setDataSourceList(installed_products);

		if (product_categories) {
			const productCategoryMap = new Map();
			product_categories.forEach((v: { name: string; label: string }) => {
				productCategoryMap.set(v.name, v.label);
			});
			setProductCategories(productCategoryMap);
		}
	}, []);

	const { loading: filteredGridLoading, refetch: refetchFilteredGridInstalledProducts } =
		useGetDistinctFilteredInstalledProductsQuery({
			variables: {
				where: productsFilter,
				user_id: loggedUserValue.id
			},
			skip: !gridView,
			errorPolicy: "none",
			fetchPolicy: "network-only",
			nextFetchPolicy: "network-only",
			notifyOnNetworkStatusChange: true,
			onError: handleOnErrorGetFilteredInstalledProducts,
			onCompleted: handleOnCompleteGetDistinctFilteredInstalledProducts
		});

	const { loading: filteredListLoading, refetch: refetchFilteredListInstalledProducts } =
		useGetFilteredInstalledProductsQuery({
			variables: {
				where: productsFilter
			},
			skip: gridView,
			errorPolicy: "none",
			fetchPolicy: "network-only",
			nextFetchPolicy: "network-only",
			notifyOnNetworkStatusChange: true,
			onError: handleOnErrorGetFilteredInstalledProducts,
			onCompleted: handleOnCompleteGetFilteredInstalledProducts
		});

	// Add the sync function after the query declaration
	const syncExternalDevices = useCallback(async () => {
		try {
			setSyncing(true);
			// Get unique properties from installed products data
			const installed_products = dataSourceList || [];

			if (installed_products && installed_products.length > 0) {
				// Create a map of unique properties with their partner info
				const uniqueProperties = new Map();

				installed_products.forEach(installedProduct => {
					const property = installedProduct.property;
					if (property && !uniqueProperties.has(property.id) && property.partner_id != null) {
						uniqueProperties.set(property.id, {
							property_id: property.id,
							partner_association: property.partner_association || "",
							account_id: property.partner_id
						});
					}
				});

				// Call the Hasura action for each unique property sequentially to prevent network congestion
				for (const propertyData of uniqueProperties.values()) {
					try {
						await externalDevicesMutation({
							variables: {
								property_id: propertyData.property_id,
								partner_association: propertyData.partner_association,
								account_id: propertyData.account_id
							}
						});
						console.log("Synced external devices for property:", propertyData);
					} catch (error) {
						console.error(`Error syncing external devices for property ${propertyData.property_id}:`, error);
					}
				}
				gridView ? await refetchFilteredGridInstalledProducts() : await refetchFilteredListInstalledProducts();

				setSyncing(false);
				console.log("External devices sync completed");
			} else {
				setSyncing(false);
			}
		} catch (error) {
			setSyncing(false);
			console.error("Error syncing external devices:", error);
		}
	}, [
		externalDevicesMutation,
		refetchFilteredListInstalledProducts,
		refetchFilteredGridInstalledProducts,
		dataSourceList,
		gridView
	]);

	const handleTableRowClick = useCallback(
		(recordId: string) => {
			handleOnClickDevice(recordId);
		},
		[handleOnClickDevice]
	);

	const handleDateRangeChange = useCallback(
		(dates: any, dateStrings: [string, string]) => {
			if (dates?.length) {
				const startDate = dates[0] ? dates[0].toDate() : null;
				const endDate = dates[1] ? dates[1].toDate() : null;
				setDateRange(startDate, endDate);
			} else {
				setDateRange(null, null);
			}
		},
		[setDateRange]
	);

	// Sync external devices when properties are selected and data is available
	useEffect(() => {
		if (gridView) return;
		// Only sync if properties have changed and we have data
		const propertiesChanged =
			JSON.stringify(selectedPropertiesFilter) !== JSON.stringify(lastSyncedPropertiesRef.current);

		if (propertiesChanged && selectedPropertiesFilter.length > 0 && dataSourceList.length > 0) {
			console.log("Triggering sync for selected properties:", selectedPropertiesFilter);
			lastSyncedPropertiesRef.current = [...selectedPropertiesFilter];
			syncExternalDevices();
		}
	}, [selectedPropertiesFilter, syncExternalDevices, gridView]);

	const [propertiesOptions, propertyDropdownLabel = "Property"] = useMemo(() => {
		if (!loggedUserValue?.user_properties?.length) return [[], "Property"];

		const properties = loggedUserValue.user_properties.map(up => up.property);
		const areAllOrNoneSelected =
			selectedPropertiesFilter.length === 0 || selectedPropertiesFilter.length === properties.length;

		const items = buildCheckboxMenuItems(
			properties.map(p => ({ key: p?.id ?? "", label: p?.name ?? "" })),
			areAllOrNoneSelected ? [] : selectedPropertiesFilter,
			{ key: "property-dropdown-option-all", label: "All properties", checked: areAllOrNoneSelected },
			"property-dropdown-option"
		);

		const firstSelectedName = properties.find(
			p => !areAllOrNoneSelected && selectedPropertiesFilter.includes(p?.id ?? "")
		)?.name;
		return [items, createLabelForFilters("Property", selectedPropertiesFilter, firstSelectedName)];
	}, [selectedPropertiesFilter, loggedUserValue.user_properties]);

	const [categoriesOptions, categoryDropdownLabel = "Category"] = useMemo(() => {
		if (!productCategories?.size) return [[], "Category"];

		const categoryItems = Array.from(productCategories.entries()).map(([key, value]) => ({ key, label: value }));
		const items = buildCheckboxMenuItems(
			categoryItems,
			selectedCategoriesFilter,
			{ key: "category-dropdown-option-all", label: "All Categories", checked: selectedCategoriesFilter.length === 0 },
			"category-dropdown-option"
		);

		const selectedValues = selectedCategoriesFilter.map(v => productCategories.get(v));
		return [items, createLabelForFilters("Category", selectedValues)];
	}, [productCategories, selectedCategoriesFilter]);

	// Temporary. See MWA-260. No need to do any calculations for now
	// const [statusOptions, statusDropdownLabel = "Warranty Status"] =
	//   useMemo(() => {
	//     const { installed_products } = installedProductsData || {};
	//     if (installed_products?.length) {
	//       const productUniqueStatus = installed_products.filter(
	//         (element, index, self) =>
	//           index ===
	//           self.findIndex((t) => t.warranty_status === element.warranty_status)
	//       );

	//       const productStatusValues = productUniqueStatus.map(
	//         (p) => p.warranty_status
	//       );

	//       if (productStatusValues?.length) {
	//         const productStatus: MenuProps["items"] = productStatusValues.reduce(
	//           (options, status, currentindex, array) => {
	//             const optionId = `status-dropdown-option-${currentindex + 1}`;

	//             const isSelected = selectedWarrantyStatusFilter.includes(status);

	//             const newOption = {
	//               key: status,
	//               icon: (
	//                 <div>
	//                   <input
	//                     className="custom-checkbox"
	//                     id={optionId}
	//                     type="checkbox"
	//                     onClick={(e) => e.stopPropagation()}
	//                     checked={isSelected}
	//                     onChange={voidOnChange}
	//                   />
	//                   <label htmlFor={optionId} className="custom-checkbox-label">
	//                     {status}
	//                   </label>
	//                 </div>
	//               ),
	//             };

	//             options.push(newOption);

	//             return options;
	//           },
	//           [
	//             {
	//               key: "status-dropdown-option-all",
	//               icon: (
	//                 <div>
	//                   <input
	//                     className="custom-checkbox"
	//                     id="status-dropdown-option-all"
	//                     type="checkbox"
	//                     onClick={(e) => e.stopPropagation()}
	//                   />
	//                   <label
	//                     htmlFor="status-dropdown-option-all"
	//                     className="custom-checkbox-label"
	//                   >
	//                     View All
	//                   </label>
	//                 </div>
	//               ),
	//             },
	//           ]
	//         );

	//         return [
	//           productStatus,
	//           createLabelForFilters(
	//             "Warranty Status",
	//             selectedWarrantyStatusFilter
	//           ),
	//         ];
	//       }

	//       return [];
	//     }

	//     return [];
	//   }, [installedProductsData, selectedWarrantyStatusFilter]);

	const [deviceStatusOptions, deviceStatusDropdownLabel = "Device Status"] = useMemo(() => {
		const allSelected = selectedDeviceStatusFilter.length === 0;
		const firstSelectedLabel = DEVICE_STATUS_FILTER_OPTIONS.find(o =>
			selectedDeviceStatusFilter.includes(o.key)
		)?.label;

		const items = buildCheckboxMenuItems(
			DEVICE_STATUS_FILTER_OPTIONS,
			selectedDeviceStatusFilter,
			{ key: "device-status-dropdown-option-all", label: "All Statuses", checked: allSelected },
			"device-status-dropdown-option"
		);

		return [items, createLabelForFilters("Device Status", selectedDeviceStatusFilter, firstSelectedLabel)];
	}, [selectedDeviceStatusFilter]);

	if (filteredGridLoading || filteredListLoading || syncing) {
		return <div>Loading...</div>;
	}

	return (
		<div className="products-screen-container">
			<span className="page-title">{"My Devices"}</span>
			<div className="products-content-container" ref={scrollContainerRef}>
				<div className="products-filter-group">
					<div className="search-products-row">
						<div className="products-search-container">
							<Input
								className="product-search-input-wrapper"
								name="searchProduct"
								placeholder="Search..."
								classNames={{ input: "product-search-input" }}
								value={searchTerm} // Use Zustand search term
								autoComplete="off"
								onChange={handleSearchTermChange}
								prefix={<HiSearch color={"#67748E"} size={"16px"} />}
								allowClear
							/>
						</div>
						<ViewFilter gridView={gridView} setGridView={setGridView} />
					</div>
					<div className="products-filters-row">
						<Dropdown
							menu={{
								items: propertiesOptions,
								selectable: true,
								multiple: true,
								onSelect: args => handleOnSelectPropertiesFilter(args.selectedKeys),
								onDeselect: args => handleOnSelectPropertiesFilter(args.selectedKeys),
								selectedKeys:
									selectedPropertiesFilter.length === 0 ||
									selectedPropertiesFilter.length === loggedUserValue.user_properties.length
										? []
										: selectedPropertiesFilter
							}}
							arrow
							className="products-dropdown"
							overlayClassName="products-dropdown-overlay"
							trigger={["click"]}
							open={propertiesDropdownOpen}
							onOpenChange={(open: boolean, info: { source: "trigger" | "menu" }) => {
								if (info.source === "trigger" && open === false) {
									setPropertiesDropdownOpen(false);
								}
							}}
							disabled={propertiesOptions.length === 0}
						>
							<Button
								onClick={() => {
									setPropertiesDropdownOpen(previousValue => !previousValue);
								}}
							>
								{propertyDropdownLabel}
								<img
									src={process.env.PUBLIC_URL + "/assets/images/icons/black-chevron-down.svg"}
									title={propertyDropdownLabel}
									className="chevron-button"
								/>
							</Button>
						</Dropdown>
						<Dropdown
							menu={{
								items: categoriesOptions,
								selectable: true,
								multiple: true,
								onSelect: args => handleOnSelectCategoriesFilter(args.selectedKeys),
								onDeselect: args => handleOnSelectCategoriesFilter(args.selectedKeys),
								selectedKeys: selectedCategoriesFilter,
								defaultSelectedKeys: selectedCategoriesFilter
							}}
							arrow
							className="products-dropdown"
							trigger={["click"]}
							open={categoriesDropdownOpen}
							onOpenChange={(open: boolean, info: { source: "trigger" | "menu" }) => {
								if (info.source === "trigger" && open === false) {
									setCategoriesDropdownOpen(false);
								}
							}}
							disabled={categoriesOptions.length === 0}
						>
							<Button
								onClick={() => {
									setCategoriesDropdownOpen(previousValue => !previousValue);
								}}
							>
								{categoryDropdownLabel}
								<img
									src={process.env.PUBLIC_URL + "/assets/images/icons/black-chevron-down.svg"}
									title={categoryDropdownLabel}
									className="chevron-button"
								/>
							</Button>
						</Dropdown>
						<Dropdown
							menu={{
								items: deviceStatusOptions,
								selectable: true,
								multiple: true,
								onSelect: args => handleOnSelectDeviceStatusFilter(args.selectedKeys),
								onDeselect: args => handleOnSelectDeviceStatusFilter(args.selectedKeys),
								selectedKeys: selectedDeviceStatusFilter
							}}
							arrow
							className="products-dropdown"
							overlayClassName="products-dropdown-overlay"
							trigger={["click"]}
							open={deviceStatusDropdownOpen}
							onOpenChange={(open: boolean, info: { source: "trigger" | "menu" }) => {
								if (info.source === "trigger" && open === false) {
									setDeviceStatusDropdownOpen(false);
								}
							}}
						>
							<Button
								onClick={() => {
									setDeviceStatusDropdownOpen(previousValue => !previousValue);
								}}
							>
								{deviceStatusDropdownLabel}
								<img
									src={process.env.PUBLIC_URL + "/assets/images/icons/black-chevron-down.svg"}
									title={deviceStatusDropdownLabel}
									className="chevron-button"
								/>
							</Button>
						</Dropdown>

						{/* Temporary. See MWA-260 */}
						{/* <Dropdown
              menu={{
                items: statusOptions,
                selectable: true,
                multiple: true,
                onSelect: (args) => handleOnSelectStatusFilter(args),
                onDeselect: (args) => handleOnSelectStatusFilter(args),
                selectedKeys: selectedWarrantyStatusFilter,
              }}
              arrow
              className="products-dropdown status"
              trigger={["click"]}
              open={statusDropdownOpen}
              onOpenChange={(
                open: boolean,
                info: { source: "trigger" | "menu" }
              ) => {
                if (info.source === "trigger" && open === false) {
                  setStatusDropdownOpen(false);
                }
              }}
            >
              <Button
                onClick={() => {
                  setStatusDropdownOpen((previousValue) => !previousValue);
                }}
              >
                {statusDropdownLabel} 
                <img
                  src={
                    process.env.PUBLIC_URL +
                    "/assets/images/icons/black-chevron-down.svg"
                  }
                  title={statusDropdownLabel}
                  className="chevron-button"
                />
              </Button>
            </Dropdown> */}

						<RangePicker
							name="installation-date-picker"
							showTime
							format={"MM/DD/YYYY"}
							placeholder={["Install Date", "Till Now"]}
							allowEmpty={[false, true]}
							allowClear
							className="products-installation-date"
							onChange={handleDateRangeChange}
							value={[
								installationDateRange.start ? dayjs(installationDateRange.start) : null,
								installationDateRange.end ? dayjs(installationDateRange.end) : null
							]}
						/>

						<ViewFilter gridView={gridView} setGridView={setGridView} />
					</div>
				</div>
				<div className="products-table">
					{gridView ? (
						<Row className="products-grid-container" align="stretch">
							{dataSource.map(element => {
								const {
									id,
									product,
									property,
									installation_date,
									location,
									device_status,
									property_count = 1
								} = element;

								const arePropertiesFiltered =
									selectedPropertiesFilter.length !== 0 &&
									selectedPropertiesFilter.length !== loggedUserValue.user_properties.length;
								const adjustedPropertyCount = !arePropertiesFiltered
									? property_count
									: Math.min(
											selectedPropertiesFilter.length,
											product.installed_products_aggregate?.aggregate?.count || 0
										);

								return (
									<InstalledProductCard
										key={`installed-product-${element.id}`}
										product={product}
										installed_product={element}
										property={property!}
										location={location}
										installation_date={installation_date}
										device_status={device_status}
										property_count={adjustedPropertyCount}
										arePropertiesFiltered={arePropertiesFiltered}
									/>
								);
							})}
						</Row>
					) : (
						<div className="products-table-container">
							<Table
								dataSource={dataSourceList}
								columns={tableColumns}
								rowKey={record => record.id}
								onRow={record => ({
									onClick: () => handleTableRowClick(record.id)
								})}
							/>
						</div>
					)}
				</div>
				<ScrollToTopButton parentRef={scrollContainerRef} />
			</div>
		</div>
	);
};

export default InstalledProducts;
